import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import OrderService from '../services/OrderService';
import { SupplierOrder, OrderItem, CatalogItem } from '../types';
import globalErrorHandler from '../../../core/services/GlobalErrorHandler';
import { HttpError } from '../../../core/services/HttpService';
import { StatusBadge } from './OrderStatusBadge';
import { SuccessOverlay, SuccessType } from './OrderSuccessOverlay';
import { formatDate, formatDateTime } from './OrderListView';
import styles from './OrdersPage.module.css';

const COLORS = {
  navy: '#0E1333',
  blue: '#3B82F6',
  green: '#10B981',
  muted: '#64748b',
  text: '#334155',
  border: '#e0e3ef',
  white: '#ffffff',
  red: '#EF4444',
  slate: '#5271A5',
  bgHover: '#f1f5f9',
};

function DeliveryTimeline({ order }: { order: SupplierOrder }): React.JSX.Element {
  const steps = [
    { label: 'Order sent', done: true, active: false },
    { label: 'In transit', done: true, active: true },
    { label: 'Delivered', done: false, active: false },
  ];

  return (
    <div className={styles.deliveryTimelineCard}>
      <div className={styles.deliveryTimelineHeader}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={COLORS.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
        <span className={styles.deliveryTimelineHeaderBold}>Expected delivery: {formatDate(order.expected_delivery)}</span>
      </div>
      <div className={styles.deliveryTimelineSteps}>
        {steps.map((step, idx) => (
          <React.Fragment key={step.label}>
            <div className={styles.deliveryStep}>
              {step.active ? (
                <motion.div
                  animate={{ boxShadow: ['0 0 0 0 rgba(59,130,246,0.4)', '0 0 0 8px rgba(59,130,246,0)', '0 0 0 0 rgba(59,130,246,0.4)'] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className={styles.deliveryStepDot}
                  style={{ background: COLORS.blue }}
                />
              ) : (
                <div
                  className={styles.deliveryStepDot}
                  style={{ background: step.done ? COLORS.blue : '#D1D5DB' }}
                />
              )}
              <span
                className={styles.deliveryStepLabel}
                style={{ color: step.done || step.active ? COLORS.blue : COLORS.muted }}
              >
                {step.label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div
                className={styles.deliveryStepLine}
                style={{ background: step.done ? COLORS.blue : '#D1D5DB' }}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function QuantityControl({ value, onChange }: {
  value: number;
  onChange: (newVal: number) => void;
}): React.JSX.Element {
  return (
    <div className={styles.quantityRow}>
      <button
        onClick={() => onChange(Math.max(1, value - 1))}
        className={styles.quantityBtn}
      >
        -
      </button>
      <span className={styles.quantityValue}>{value}</span>
      <button
        onClick={() => onChange(value + 1)}
        className={styles.quantityBtn}
      >
        +
      </button>
    </div>
  );
}

export function DraftView({ order, onSend, onRefreshDetail, onAddItemMode }: {
  order: SupplierOrder;
  onSend: () => void;
  onRefreshDetail: () => void;
  onAddItemMode: () => void;
}): React.JSX.Element {
  const [successType, setSuccessType] = useState<SuccessType>(null);
  const [sendingOrder, setSendingOrder] = useState(false);
  const debounceTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  const handleRemoveItem = useCallback(async (itemId: number) => {
    try {
      await OrderService.removeItem(order.id, itemId);
      onRefreshDetail();
    } catch (err) { if (err instanceof HttpError) globalErrorHandler.handle(err); }
  }, [order.id, onRefreshDetail]);

  const handleQuantityChange = useCallback((itemId: number, newQuantity: number) => {
    if (debounceTimers.current[itemId]) {
      clearTimeout(debounceTimers.current[itemId]);
    }
    debounceTimers.current[itemId] = setTimeout(async () => {
      try {
        await OrderService.updateItem(order.id, itemId, { quantity_ordered: newQuantity });
        onRefreshDetail();
      } catch (err) { if (err instanceof HttpError) globalErrorHandler.handle(err); }
    }, 300);
  }, [order.id, onRefreshDetail]);

  const handleSendOrder = useCallback(async () => {
    setSendingOrder(true);
    try {
      await OrderService.sendOrder(order.id);
      setSuccessType('sent');
      setTimeout(() => {
        onSend();
      }, 2000);
    } catch (err) {
      if (err instanceof HttpError) globalErrorHandler.handle(err);
      setSendingOrder(false);
    }
  }, [order.id, onSend]);

  useEffect(() => {
    return () => {
      Object.values(debounceTimers.current).forEach(clearTimeout);
    };
  }, []);

  const items = order.items || [];

  return (
    <div className={styles.draftViewContainer}>
      <SuccessOverlay type={successType} onDismiss={() => setSuccessType(null)} />

      <div className={styles.draftViewHeader}>
        <div className={styles.draftViewTitleRow}>
          <h3 className={styles.viewTitle}>{order.supplier_name}</h3>
          <StatusBadge label="Draft Order" color={COLORS.navy} bgColor="transparent" />
        </div>
        <div className={styles.viewSubtitle}>
          {items.length} items
          {order.source === 'inventory_auto' && (
            <span className={styles.viewAutoTag}>Auto-generated from inventory</span>
          )}
        </div>
      </div>

      <div className={styles.scrollArea}>
        {items.map((item) => (
          <div key={item.id} className={styles.itemCard}>
            <button
              onClick={() => handleRemoveItem(item.id)}
              className={styles.removeBtn}
            >
              X
            </button>
            <div className={styles.itemNameContainer}>
              <div className={styles.itemName}>{item.name}</div>
              <div className={styles.itemUnit}>
                {item.unit} &middot; Min: {item.quantity_min}
              </div>
            </div>
            <QuantityControl
              value={item.quantity_ordered}
              onChange={(newVal) => handleQuantityChange(item.id, newVal)}
            />
          </div>
        ))}

        <div
          onClick={onAddItemMode}
          className={styles.addItemBtn}
        >
          + Add Item from {order.supplier_name}
        </div>
      </div>

      <div className={styles.viewFooter}>
        <button
          onClick={handleSendOrder}
          disabled={sendingOrder || items.length === 0}
          className={items.length === 0 ? styles.sendBtnDisabled : styles.sendBtn}
          style={{ opacity: sendingOrder ? 0.7 : 1 }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 2L11 13" />
            <path d="M22 2L15 22L11 13L2 9L22 2Z" />
          </svg>
          Send Order
        </button>
      </div>
    </div>
  );
}

export function SentView({ order, onMarkDelivered }: {
  order: SupplierOrder;
  onMarkDelivered: () => void;
}): React.JSX.Element {
  const [markingDelivered, setMarkingDelivered] = useState(false);
  const items = order.items || [];

  const handleMarkDelivered = useCallback(async () => {
    setMarkingDelivered(true);
    try {
      await OrderService.markDelivered(order.id);
      onMarkDelivered();
    } catch (err) {
      if (err instanceof HttpError) globalErrorHandler.handle(err);
      setMarkingDelivered(false);
    }
  }, [order.id, onMarkDelivered]);

  return (
    <div className={styles.sentViewContainer}>
      <div className={styles.sentViewHeader}>
        <div className={styles.draftViewTitleRow}>
          <h3 className={styles.viewTitle}>{order.supplier_name}</h3>
          <StatusBadge label="Sent" color={COLORS.white} bgColor={COLORS.navy} />
        </div>
        <div className={styles.sentSubtitle}>
          Sent {formatDateTime(order.sent_at)}
        </div>
      </div>

      <div className={styles.scrollAreaLargeGap}>
        <DeliveryTimeline order={order} />

        {items.map((item) => (
          <div key={item.id} className={styles.itemCardSpaceBetween}>
            <div>
              <div className={styles.itemName}>{item.name}</div>
              <div className={styles.itemUnit}>{item.unit}</div>
            </div>
            <div className={styles.itemQuantity}>
              <span className={styles.itemQuantityText}>{item.quantity_ordered}x</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={COLORS.blue} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17L4 12" />
              </svg>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.viewFooter}>
        <button
          onClick={handleMarkDelivered}
          disabled={markingDelivered}
          className={styles.markDeliveredBtn}
          style={{ opacity: markingDelivered ? 0.7 : 1 }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
          Mark as Delivered
        </button>
      </div>
    </div>
  );
}

export function CatalogPanel({ order, onBack, onItemAdded }: {
  order: SupplierOrder;
  onBack: () => void;
  onItemAdded: () => void;
}): React.JSX.Element {
  const [catalogItems, setCatalogItems] = useState<CatalogItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [addingItemId, setAddingItemId] = useState<number | null>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchCatalog = useCallback(async (search?: string) => {
    setLoading(true);
    try {
      const items = await OrderService.getCatalog(order.supplier_id, {
        exclude_order: order.id,
        search: search || undefined,
      });
      setCatalogItems(items);
    } catch (err) { if (err instanceof HttpError) globalErrorHandler.handle(err); }
    setLoading(false);
  }, [order.supplier_id, order.id]);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      fetchCatalog(value);
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, []);

  const handleAddItem = async (catalogItem: CatalogItem) => {
    setAddingItemId(catalogItem.id);
    try {
      await OrderService.addItem(order.id, { inventory_item_id: catalogItem.id, quantity: 1 });
      onItemAdded();
      fetchCatalog(searchQuery);
    } catch (err) { if (err instanceof HttpError) globalErrorHandler.handle(err); }
    setAddingItemId(null);
  };

  return (
    <div className={styles.catalogContainer}>
      <div className={styles.catalogHeader}>
        <div className={styles.catalogBackRow}>
          <button onClick={onBack} className={styles.catalogBackBtn}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19L5 12L12 5" />
            </svg>
          </button>
          <div>
            <div className={styles.catalogSupplierName}>{order.supplier_name}</div>
            <div className={styles.catalogItemCount}>{catalogItems.length} items available</div>
          </div>
        </div>
        <div className={styles.catalogSearchWrapper}>
          <svg
            width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke={COLORS.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            className={styles.catalogSearchIcon}
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search items..."
            className={styles.catalogSearchInput}
          />
        </div>
      </div>

      <div className={styles.catalogItemsList}>
        {loading && catalogItems.length === 0 && (
          <div className={styles.catalogLoadingEmpty}>Loading...</div>
        )}
        {!loading && catalogItems.length === 0 && (
          <div className={styles.catalogLoadingEmpty}>No items found</div>
        )}
        {catalogItems.map((item) => (
          <div key={item.id} className={styles.catalogItemRow}>
            <button
              onClick={() => handleAddItem(item)}
              disabled={addingItemId === item.id}
              className={styles.catalogAddBtn}
              style={{ opacity: addingItemId === item.id ? 0.5 : 1 }}
            >
              +
            </button>
            <div className={styles.itemNameContainer}>
              <div className={styles.catalogItemName}>{item.name}</div>
              <div className={styles.catalogItemSub}>
                {item.pack_size} &middot; Min: {item.min_stock}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
